import { useMutation, useQueryClient } from 'react-query';
import { updateTradingPhotoCards } from '../apis/updateTradingPhotoCards';
import type { TradingPhotoCardType } from '../types/TradingPhotoCardType';
import { QUERY_KEYS } from '../constants/QueryKeys';
import { formatValue } from 'react-currency-input-field';
import { intlConfig } from '@pocapan/shared/utils/CurrencyUtil';
import useUserQuery from '../../hooks/queries/useUserQuery';
import { t } from 'i18next';

interface UpdateTradingPhotoCardsParams {
  tradingPhotoCardId: string;
  price: number | string | null;
  category: string;
  status: string;
}

export const useUpdateTradingPhotoCardsMutation = () => {
  const queryClient = useQueryClient();
  const { data: user } = useUserQuery();

  return useMutation({
    mutationFn: updateTradingPhotoCards,
    onMutate: async (newData: UpdateTradingPhotoCardsParams) => {
      await queryClient.cancelQueries(QUERY_KEYS.tradingPhotoCardsAll('sale'));

      const previousData = queryClient.getQueryData(QUERY_KEYS.tradingPhotoCardsAll('sale'));

      queryClient.setQueryData(QUERY_KEYS.tradingPhotoCardsAll('sale'), (old: any) => {
        if (!old || !old.tradingPhotoCards) return old;

        return {
          ...old,
          tradingPhotoCards: old.tradingPhotoCards.map((item: TradingPhotoCardType) => {
            if (item.tradingPhotoCardId === newData.tradingPhotoCardId) {
              return {
                ...item,
                price:
                  newData.price === null ||
                  newData.price === undefined ||
                  (typeof newData.price === 'string' && newData.price === '')
                    ? t('NewTradingPhotoCards.pricePlaceholder')
                    : formatValue({
                        value: String(newData.price),
                        intlConfig: intlConfig(user) || { locale: 'ko-KR', currency: 'KRW' }
                      }),
                category: newData.category,
                status: newData.status
              };
            }
            return item;
          })
        };
      });

      return { previousData };
    },
    onError: (_error, _newData, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(QUERY_KEYS.tradingPhotoCardsAll('sale'), context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(QUERY_KEYS.tradingPhotoCardsAll('sale'));
    }
  });
};
